import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    // Guard against SSR / prerender contexts where localStorage is undefined.
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return null;
    return localStorage.getItem('roofmate_token');
  });

  const getAuthHeaders = useCallback(() => {
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, [token]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setUser(res.data);
    } catch (e) {
      // Distinguish "signed out because of another device login" from
      // routine auth failures (expired token / never logged in) so we can
      // surface the dedicated modal via localStorage flag → LoginPage
      // reads it and shows the "Signed out on another device" banner.
      if (e?.response?.status === 401 && e?.response?.data?.detail === 'SESSION_INVALIDATED') {
        try { localStorage.setItem('roofmate_session_kicked', '1'); } catch (_) { /* private mode */ }
      }
      setUser(null);
      setToken(null);
      localStorage.removeItem('roofmate_token');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, opts = {}) => {
    try {
      const body = { email, password };
      // opts.forceTakeover is set by LoginPage after the user confirms the
      // "You're signed in on another device — continue here?" prompt.
      if (opts.forceTakeover) body.force_takeover = true;
      const res = await axios.post(`${API}/auth/login`, body, { withCredentials: true });
      // Single-session takeover confirmation — asked before OTP is sent.
      if (res.data.requires_takeover_confirmation) {
        return {
          success: false,
          requires_takeover_confirmation: true,
          email: res.data.email,
          message: res.data.message,
        };
      }
      // Check if 2FA OTP is required
      if (res.data.requires_otp) {
        return { success: false, requires_otp: true, email: res.data.email, message: res.data.message };
      }
      const accessToken = res.data.access_token;
      if (accessToken) {
        setToken(accessToken);
        localStorage.setItem('roofmate_token', accessToken);
      }
      setUser(res.data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const verifyOtp = async (email, code) => {
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { email, code }, { withCredentials: true });
      const accessToken = res.data.access_token;
      if (accessToken) {
        setToken(accessToken);
        localStorage.setItem('roofmate_token', accessToken);
      }
      setUser(res.data);
      return { success: true };
    } catch (e) {
      // Retry once after short delay (OTP record may not be written yet)
      if (e.response?.status === 400) {
        await new Promise(r => setTimeout(r, 1000));
        try {
          const res = await axios.post(`${API}/auth/verify-otp`, { email, code }, { withCredentials: true });
          const accessToken = res.data.access_token;
          if (accessToken) {
            setToken(accessToken);
            localStorage.setItem('roofmate_token', accessToken);
          }
          setUser(res.data);
          return { success: true };
        } catch (e2) {
          return { success: false, error: formatApiErrorDetail(e2.response?.data?.detail) };
        }
      }
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const resendOtp = async (email) => {
    try {
      const res = await axios.post(`${API}/auth/resend-otp`, { email });
      return { success: true, message: res.data.message };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders(), withCredentials: true });
    } catch {
      // Fire-and-forget: server-side cookie clear is best-effort. Client state is cleared below regardless.
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('roofmate_token');
    // Voluntary logout should never surface the "signed out on another
    // device" banner — clear the flag in case an earlier kick left it
    // behind in this browser's localStorage.
    localStorage.removeItem('roofmate_session_kicked');
  };

  const forceLogout = useCallback((message) => {
    // Ask the backend to invalidate the current cookies. The user's own sid
    // may already be stale (SESSION_INVALIDATED case) but /auth/logout is
    // idempotent and importantly clears the httpOnly access_token cookie —
    // without this the still-valid cookie would silently re-authenticate
    // the kicked device once the winning device logs out.
    try {
      axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders(), withCredentials: true })
        .catch(() => { /* best-effort */ });
    } catch (_) { /* ignore */ }
    setUser(null);
    setToken(null);
    localStorage.removeItem('roofmate_token');
    if (message) {
      localStorage.setItem('roofmate_deactivated_msg', message);
    }
  }, [getAuthHeaders]);

  const apiCall = useCallback(async (method, url, data = null, opts = {}) => {
    // Binary downloads (responseType: 'blob' / 'arraybuffer') go through
    // fetch() instead of axios. Reason: some platform-injected monitoring
    // scripts wrap XMLHttpRequest and try to read xhr.responseText for every
    // request, which throws on binary responseType. fetch() bypasses that
    // entirely while preserving auth + credentials.
    if (opts?.responseType === 'blob' || opts?.responseType === 'arraybuffer') {
      const upper = String(method || 'get').toUpperCase();
      const headers = {
        ...(opts?.headers || {}),
        ...getAuthHeaders(),
        ...(upper === 'GET' || upper === 'DELETE' ? {} : { 'Content-Type': 'application/json' }),
      };
      const res = await fetch(`${API}${url}`, {
        method: upper,
        credentials: 'include',
        headers,
        body: upper === 'GET' || upper === 'DELETE' ? undefined : JSON.stringify(data),
      });
      // Defensive clone — third-party / platform monitoring scripts on the page
      // can eagerly read the response body for telemetry, which consumes the
      // underlying stream before our handler runs and yields a confusing
      // "Body has already been consumed" error on the legitimate read below.
      // Cloning immediately gives us an independent copy.
      let working = res;
      try { working = res.clone(); } catch (_) { /* if even clone fails, fall back to res */ }
      if (!working.ok) {
        let text = '';
        try { text = await working.text(); } catch (_) { /* body unreadable */ }
        let detail = text;
        try { detail = JSON.parse(text)?.detail || text; } catch (_) { /* keep raw */ }
        const err = new Error(typeof detail === 'string' && detail ? detail : 'Request failed');
        err.response = { status: working.status, data: { detail } };
        throw err;
      }
      const body = opts.responseType === 'blob' ? await working.blob() : await working.arrayBuffer();
      return { data: body, status: working.status, headers: Object.fromEntries(working.headers.entries()) };
    }

    const config = {
      ...opts,
      headers: { ...(opts?.headers || {}), ...getAuthHeaders() },
      withCredentials: true,
    };
    try {
      if (method === 'get' || method === 'delete') {
        return await axios[method](`${API}${url}`, config);
      }
      return await axios[method](`${API}${url}`, data, config);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 401 && detail === 'SESSION_INVALIDATED') {
        // Kicked by a later login on another device — flag it so LoginPage
        // shows the "Signed out on another device" banner, then force-logout.
        try { localStorage.setItem('roofmate_session_kicked', '1'); } catch (_) { /* private mode */ }
        forceLogout();
        throw e;
      }
      if (e.response?.status === 403 && detail === 'ACCOUNT_DEACTIVATED') {
        forceLogout('Your account has been deactivated by an administrator.');
        throw e;
      }
      if (e.response?.status === 403 && detail === 'TENANT_DEACTIVATED') {
        forceLogout('Your organization has been deactivated. Please contact the administrator.');
        throw e;
      }
      if (e.response?.status === 403 && detail === 'TENANT_LOCKED') {
        forceLogout("Your organization's subscription is inactive. Please ask your administrator to update payment.");
        throw e;
      }
      throw e;
    }
  }, [getAuthHeaders, forceLogout]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, verifyOtp, resendOtp, apiCall, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
